import type { ReactElement } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Animated inline-SVG illustrations for the pricing add-on cards, replacing the
 * old dark stock photos. One distinct-but-cohesive motif per add-on so the row
 * never shows the same animation twice. Same house palette and reduced-motion
 * discipline as the marketing diagrams: fixed viewBox (CLS-safe), coherent
 * static end state under prefers-reduced-motion, no new dependency.
 *
 *  - "onboarding": a checklist whose items tick in sequence (guided setup call).
 *  - "call": a phone with slow, calm signal rings (an extra review call).
 *  - "briefing": two people with a document passing between them, drawn on
 *    scroll (briefing your executor or solicitor).
 *  - "pack": a sealed document whose wax seal stamps down and ticks (the
 *    physical Sealed Continuity Pack held by a solicitor).
 */

export type AddonIllustrationKind = "onboarding" | "call" | "briefing" | "pack";

const TEAL = "#2dd4bf";
const SLATE = "#94a3b8";
const INK = "#0f172a";

const VIEWBOX = "0 0 240 128";
const svgProps = {
  viewBox: VIEWBOX,
  className: "w-full h-full",
  preserveAspectRatio: "xMidYMid meet" as const,
  role: "img" as const,
};

const seen = { once: true, amount: 0.4 };

function OnboardingArt({ reduce }: { reduce: boolean }) {
  const rows = [48, 68, 88];
  return (
    <svg {...svgProps} aria-label="Guided setup with a specialist, step by step">
      {/* Checklist card */}
      <rect x={78} y={26} width={84} height={76} rx={7} fill={INK} stroke={TEAL} strokeWidth={2} />
      {rows.map((y, i) => (
        <g key={y}>
          <circle cx={95} cy={y} r={7} fill="none" stroke={reduce ? TEAL : SLATE} strokeWidth={2} />
          <motion.path
            d={`M91 ${y} l3 3 l6 -7`}
            fill="none"
            stroke={TEAL}
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={reduce ? undefined : { pathLength: 0 }}
            whileInView={reduce ? undefined : { pathLength: 1 }}
            viewport={seen}
            transition={{ duration: 0.3, delay: 0.3 + i * 0.35 }}
          />
          <line x1={110} y1={y} x2={150} y2={y} stroke={SLATE} strokeWidth={2} strokeLinecap="round" />
        </g>
      ))}
    </svg>
  );
}

function CallArt({ reduce }: { reduce: boolean }) {
  return (
    <svg {...svgProps} aria-label="A scheduled call with a UK-based specialist">
      <circle cx={120} cy={64} r={42} fill="rgba(45,212,191,0.07)" />
      {[0, 0.8, 1.6].map((delay, i) => (
        <motion.circle
          key={i}
          cx={120}
          cy={64}
          r={28}
          fill="none"
          stroke={TEAL}
          strokeWidth={1.5}
          initial={{ opacity: reduce ? 0.28 : 0, r: 28 }}
          animate={reduce ? { opacity: 0.28 } : { r: [28, 54], opacity: [0.55, 0] }}
          transition={reduce ? undefined : { duration: 2.6, repeat: Infinity, delay, ease: "easeOut" }}
        />
      ))}
      <circle cx={120} cy={64} r={28} fill={INK} stroke={TEAL} strokeWidth={2} />
      <g transform="translate(105.6 49.6) scale(1.2)">
        <path
          d="M6.62 10.79a15.53 15.53 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.57 1 1 0 0 1-.24 1.02z"
          fill={TEAL}
        />
      </g>
    </svg>
  );
}

/** Head-and-shoulders person glyph centred at (cx, cy). */
function Person({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth={2}>
      <circle cx={cx} cy={cy - 8} r={9} />
      <path d={`M${cx - 15} ${cy + 20} a 15 13 0 0 1 30 0`} strokeLinecap="round" />
    </g>
  );
}

function BriefingArt({ reduce }: { reduce: boolean }) {
  return (
    <svg {...svgProps} aria-label="A briefing call for your executor or solicitor">
      <Person cx={80} cy={58} color={TEAL} />
      <Person cx={160} cy={58} color={SLATE} />

      {/* Connecting arc drawn on scroll */}
      <motion.path
        d="M96 50 C 116 24, 124 24, 144 50"
        fill="none"
        stroke={TEAL}
        strokeWidth={1.5}
        strokeDasharray="4 5"
        initial={reduce ? undefined : { pathLength: 0 }}
        whileInView={reduce ? undefined : { pathLength: 1 }}
        viewport={seen}
        transition={{ duration: 0.7, delay: 0.2 }}
      />

      {/* Document handed across, settling at the midpoint */}
      <motion.g
        style={{ transformBox: "fill-box", transformOrigin: "center" }}
        initial={reduce ? undefined : { scale: 0, opacity: 0 }}
        whileInView={reduce ? undefined : { scale: [0, 1.12, 1], opacity: 1 }}
        viewport={seen}
        transition={{ duration: 0.5, delay: 0.75, times: [0, 0.7, 1], ease: "easeOut" }}
      >
        <rect x={110} y={20} width={20} height={26} rx={3} fill={INK} stroke={TEAL} strokeWidth={2} />
        <line x1={114} y1={28} x2={126} y2={28} stroke={SLATE} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={114} y1={33} x2={126} y2={33} stroke={SLATE} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={114} y1={38} x2={122} y2={38} stroke={SLATE} strokeWidth={1.5} strokeLinecap="round" />
      </motion.g>
    </svg>
  );
}

function PackArt({ reduce }: { reduce: boolean }) {
  return (
    <svg {...svgProps} aria-label="A sealed continuity pack held by your solicitor">
      <motion.g
        initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 8 }}
        whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
        viewport={seen}
        transition={{ duration: 0.5 }}
      >
        <rect x={78} y={26} width={84} height={76} rx={7} fill={INK} stroke={TEAL} strokeWidth={2} />
        <line x1={92} y1={44} x2={148} y2={44} stroke={SLATE} strokeWidth={2} strokeLinecap="round" />
        <line x1={92} y1={56} x2={148} y2={56} stroke={SLATE} strokeWidth={2} strokeLinecap="round" />
        <line x1={92} y1={68} x2={130} y2={68} stroke={SLATE} strokeWidth={2} strokeLinecap="round" />
      </motion.g>
      <motion.g
        style={{ transformBox: "fill-box", transformOrigin: "center" }}
        initial={reduce ? undefined : { scale: 0, opacity: 0 }}
        whileInView={reduce ? undefined : { scale: [0, 1.15, 1], opacity: 1 }}
        viewport={seen}
        transition={{ duration: 0.55, delay: 0.45, times: [0, 0.7, 1], ease: "easeOut" }}
      >
        <circle cx={140} cy={88} r={19} fill="rgba(45,212,191,0.15)" stroke={TEAL} strokeWidth={2} />
        <motion.path
          d="M131.5 88 l5.5 5.5 l11 -12"
          fill="none"
          stroke={TEAL}
          strokeWidth={2.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? undefined : { pathLength: 0 }}
          whileInView={reduce ? undefined : { pathLength: 1 }}
          viewport={seen}
          transition={{ duration: 0.4, delay: 1.0 }}
        />
      </motion.g>
    </svg>
  );
}

const ART: Record<AddonIllustrationKind, (p: { reduce: boolean }) => ReactElement> = {
  onboarding: OnboardingArt,
  call: CallArt,
  briefing: BriefingArt,
  pack: PackArt,
};

export default function AddonIllustration({ kind }: { kind: AddonIllustrationKind }) {
  const reduce = useReducedMotion() ?? false;
  const Art = ART[kind];
  return (
    <div className="w-full h-32 rounded-lg bg-slate-800/50 mb-4 overflow-hidden">
      <Art reduce={reduce} />
    </div>
  );
}
